<template lang="pug">
.c-stickers-page
  //- Header Section
  section.c-stickers-page__header
    .c-stickers-page__header-content
      h1.c-stickers-page__heading Stickers
      p.c-stickers-page__subtitle Manage your digital collectibles for your Shop.

    .c-stickers-page__header-actions
      el-button.c-stickers-action-btn(@click="goCollections")
        fai(type="fal" icon="images")
        span Manage collections
      el-button.c-stickers-action-btn(
        :disabled="loading || filteredItems.length === 0"
        @click="exportCsv"
      )
        fai(type="fal" icon="download")
        span Export
      el-button.c-stickers-action-btn(v-if="!stripeOnboarded" @click="onBoardOnStripe()")
        fai(type="fal" icon="external-link-alt")
        span Onboard on Stripe
      el-button.c-stickers-action-btn(type="primary" @click="$router.push(`/manage-content/media/add-stickers-v2`)")
        fai(type="fal" icon="plus-circle")
        span Add New Sticker

  setup-checklist-panel(
    v-if="$store.getters['users/isOrgAdmin']" placement="STICKER_PORTAL" :org-id="orgId"
  )

  sticker-setup-alert(
    v-if="showSetupAlert"
    @connect-stripe="onBoardOnStripe"
    @integrate-benevity="$router.push(`/manage/orgs/${orgId}/edit`)"
    @dismiss="dismissSetupAlert"
  )

  sticker-stats-cards(
    v-if="!loading && items.items.length > 0"
    :stickers="items.items"
    :org-id="orgId"
  )

  //- Content Section
  .c-stickers-content
    //- Table Section
    section.c-stickers-table-section
      CmsListToolbar(
        :tabs="viewTabs"
        :active-tab="state.currentView"
        :tabs-disabled="loading"
        v-model="state.searchTerm"
        search-placeholder="Filter stickers"
        :sort-value="state.sortOption"
        :sort-options="sortOptions"
        :selected-count="selected.length"
        @tab-change="headerFilter"
        @sort="handleSortChange"
        @clear-selection="tableRef?.clearSelection()"
      )
        template(#start)
          .c-stickers-nav
            button.c-stickers-nav-link.c-stickers-nav-link--active(type="button") Stickers
            button.c-stickers-nav-link(type="button" @click="goCollections") Collections
          .c-stickers-toolbar-divider
        template(#bulk)
          el-select.c-stickers-bulk-select(
            v-model="setAs"
            value-key="value"
            placeholder="Set as"
            @change="bulkUpdate($event.value)"
          )
            el-option(
              v-for="item in setAsOption"
              :key="item.value"
              :label="item.label"
              :value="item"
            )

      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--checkbox
            .c-skeleton-cell.c-skeleton-cell--image
            .c-skeleton-cell.c-skeleton-cell--title
            .c-skeleton-cell.c-skeleton-cell--type
            .c-skeleton-cell.c-skeleton-cell--status
            .c-skeleton-cell.c-skeleton-cell--link

      //- Table
      el-table(
        v-else
        ref="tableRef"
        :data="filteredItems"
        @row-click="$router.push(`/manage-content/media/stickers-v2/${$event.id}`)"
        @selection-change="onCheckboxChange"
        style="width: 100%"
      ).clickable-rows
        el-table-column(width="15")

        el-table-column(type="selection" width="55")

        el-table-column(width="90" label="Image")
          template(#default="scope")
            .sticker-image-cell
              img.sticker-image(:src="scope.row.url" alt="Sticker")

        el-table-column(prop="title" label="Title" min-width="170")
          template(#default="scope")
            .lh-sm
              .font-black {{ scope.row.title }}

        el-table-column(label="Type" width="110")
          template(#default="scope")
            span.type-badge(:class="typeClass(scope.row.campaignType)")
              | {{ typeLabel(scope.row.campaignType) }}

        el-table-column(label="Status" width="120")
          template(#default="scope")
            .status-badge(:class="getButtonClass(stickerState(scope.row))")
              | {{ stickerState(scope.row) }}

        el-table-column(label="Shares" width="90" align="right")
          template(#default="scope") {{ formatNumber(scope.row.shareCount) }}

        el-table-column(label="Gross Raised" width="130" align="right")
          template(#default="scope") {{ formatCurrency(scope.row.grossRaised) }}

        el-table-column(label="Net Raised" width="120" align="right")
          template(#default="scope") {{ formatCurrency(scope.row.netRaised) }}

        el-table-column(label="Impressions" width="120" align="right")
          template(#default="scope") {{ formatNumber(scope.row.viewCount) }}

        el-table-column(label="Duration" width="120")
          template(#default="scope")
            span.duration-badge(:class="`duration-badge--${duration(scope.row).tone}`")
              | {{ duration(scope.row).label }}

        el-table-column(label="GLOW" width="90")
          template(#default="scope")
            span.type-badge.type-badge--campaign(v-if="scope.row.glow") GLOW
            span.text-muted(v-else) No GLOW

        el-table-column(label="Affiliates" width="100" align="right")
          template(#default="scope") {{ formatNumber(scope.row.affiliatesCount) }}

        el-table-column(label="Created" width="120")
          template(#default="scope") {{ formatDate(scope.row.createdAt) }}

        el-table-column(label="Share Link" width="190")
          template(#default="scope")
            .share-link-cell(v-if="scope.row")
              span.share-link-text {{ getStickerShortUrl(scope.row) }}
              fai(
                type="fal"
                icon="copy"
                @click.stop="copyShareLink(scope.row)"
              ).copy-icon.size-16
            .share-link-cell(v-else)
              span.text-muted.fs-12 N/A

        el-table-column(width="15")

  //- Error State
  .c-stickers-error(v-if="state.apiError && !loading")
    .c-stickers-error__icon
      fai(type="fal" icon="exclamation-triangle").size-48
    h3.c-stickers-error__title Error loading stickers
    p.c-stickers-error__text {{ state.apiError }}
    el-button.c-stickers-error__button(
      @click="getItems"
      type="primary"
    ) Retry
</template>

<script lang="ts">
import {
  reactive, onMounted, defineComponent, ref, computed,
} from 'vue';
import { ElMessage } from 'element-plus';
import SetupChecklistPanel from '@/components/setup-checklist/SetupChecklistPanel.vue';
import CmsListToolbar from '@/components/CmsListToolbar.vue';
import dayjs from 'dayjs';
import StickerSetupAlert from './stickers/StickerSetupAlert.vue';
import StickerStatsCards from './stickers/StickerStatsCards.vue';
import shared from '../shared';
import { withTimeout } from '@/utils/withTimeout';

export default defineComponent({
  name: 'OrgStickers',
  components: {
    CmsListToolbar,
    SetupChecklistPanel,
    StickerSetupAlert,
    StickerStatsCards,
  },
  props: {
    stripe_onboarded: { type: Boolean, default: false },
  },
  setup(_props) {
    const {
      route,
      router,
      ref: sharedRef,
      getOrgStickersWithStats,
      getOrganization,
      updateOrgSticker,
      doGetStripeAuthorizeUrl,
      loading,
      approveSticker,
    } = shared();

    // Initialize loading as true to show skeleton immediately
    loading.value = true;

    const items = reactive({ items: [] as any[] });
    const { orgId } = route.params;
    const dialog = sharedRef(false);

    function goCollections() {
      router.push(`/manage/orgs/${orgId}/sticker-collections`);
    }

    const state = reactive({
      currentView: 'ALL',
      searchTerm: '',
      sortOption: null,
      activeFilters: ['ALL'],
      apiError: null,
    });

    const selected = ref([]);

    const sortOptions = [
      { label: 'Most Recent', value: 'created' },
      { label: 'Alphabetical (A-Z)', value: 'title' },
      { label: 'Cost (Low to High)', value: 'cost' },
    ];

    const setAsOption = [
      { label: 'Approved', value: 'Active' },
      { label: 'Pending', value: 'Pending' },
      { label: 'Archived', value: 'Archived' },
    ];
    const setAs = ref('');

    const tableRef = ref();
    const viewTabs = [
      { label: 'All', value: 'ALL' },
      { label: 'Approved', value: 'Active' },
      { label: 'Pending', value: 'Draft' },
      { label: 'Archived', value: 'Archived' },
    ];

    function onCheckboxChange(checkedItems) {
      selected.value = checkedItems;
    }

    function handleSortChange(selectedSort) {
      if (selectedSort && selectedSort.value) {
        state.sortOption = selectedSort;
      }
    }

    function stickerState(itm) {
      // Map the old status format to new format
      if (itm.status) {
        switch (itm.status) {
          case 'Active':
            return 'Approved';
          case 'Draft':
          case null:
          case 'Pending':
            return 'Pending';
          case 'Archived':
            return 'Archived';
          default:
            return 'Pending';
        }
      }

      // Fallback to old logic for backward compatibility
      if (itm.seen) {
        return itm.published ? 'Approved' : 'Not Approved';
      }
      return 'Pending';
    }

    function getButtonClass(currentItem) {
      switch (currentItem) {
        case 'Approved':
          return 'status-badge--approved';
        case 'Pending':
        case 'Draft':
        case 'Not Approved':
        case null:
          return 'status-badge--pending';
        case 'Archived':
          return 'status-badge--archived';
        default:
          return 'status-badge--pending';
      }
    }

    function headerFilter(filterBy) {
      state.currentView = filterBy;
      if (filterBy === 'ALL') {
        state.activeFilters = ['ALL'];
      } else {
        state.activeFilters = [filterBy];
      }
    }

    const filteredItems = computed(() => {
      let result = [...items.items];

      // Filter by status
      if (state.activeFilters.length > 0 && state.activeFilters[0] !== 'ALL') {
        result = result.filter((item) => {
          const itemState = stickerState(item);
          return state.activeFilters.includes(itemState);
        });
      }

      // Filter by search term
      if (state.searchTerm) {
        const searchLower = state.searchTerm.toLowerCase();
        result = result.filter((item) => {
          const title = item.title?.toLowerCase() || '';
          const tags = item.tags?.toLowerCase() || '';
          return title.includes(searchLower) || tags.includes(searchLower);
        });
      }

      // Sort
      if (state.sortOption) {
        const sortBy = state.sortOption.value;
        result.sort((a, b) => {
          if (sortBy === 'title') {
            return (a.title || '').localeCompare(b.title || '');
          } if (sortBy === 'cost') {
            return (a.cost || 0) - (b.cost || 0);
          } if (sortBy === 'created') {
            const dateA = new Date(a.createdAt || 0).getTime();
            const dateB = new Date(b.createdAt || 0).getTime();
            return dateB - dateA;
          }
          return 0;
        });
      }

      return result;
    });

    async function getItems() {
      try {
        loading.value = true;
        state.apiError = null;
        // A hung request (seen once for 90+ s) ends in the error state with Retry
        items.items = await withTimeout(getOrgStickersWithStats(orgId), 30_000);
      } catch (err) {
        console.error('Error fetching org stickers:', err);
        state.apiError = 'Error loading stickers. Please try again.';
      } finally {
        loading.value = false;
      }
    }

    async function bulkUpdate(type) {
      const bulkStickers = [];
      selected.value.forEach((element) => {
        const item = { id: element.id, status: type };
        bulkStickers.push(item);
      });

      try {
        await approveSticker(bulkStickers);
        ElMessage.success('Stickers updated successfully');
        await getItems();
        setAs.value = '';
        selected.value = [];
      } catch (error) {
        console.error('Error updating stickers:', error);
        ElMessage.error('Failed to update stickers');
      }
    }

    async function saveItem(input) {
      await updateOrgSticker(orgId, input);
      await getItems();
    }

    async function onBoardOnStripe() {
      const stripeLink = await doGetStripeAuthorizeUrl(orgId);
      window.location.href = stripeLink;
    }

    function getStickerShortUrl(sticker) {
      if (!sticker) return '';
      const baseUrl = import.meta.env.VITE_WEB_BASE_URL || '';
      const identifier = sticker.slug || sticker.id;
      return `${baseUrl}/web/sticker/${identifier}`;
    }

    async function copyShareLink(sticker) {
      try {
        const url = getStickerShortUrl(sticker);

        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          ElMessage.success('Share link copied to clipboard!');
        } else {
          // Fallback for older browsers or when clipboard API is not available
          const textArea = document.createElement('textarea');
          textArea.value = url;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();

          try {
            document.execCommand('copy');
            ElMessage.success('Share link copied to clipboard!');
          } catch (err) {
            console.error('Fallback copy failed:', err);
            ElMessage.error('Failed to copy share link');
          } finally {
            document.body.removeChild(textArea);
          }
        }
      } catch (error) {
        console.error('Failed to copy share link:', error);
        ElMessage.error('Failed to copy share link');
      }
    }

    const stripeOnboarded = ref(true);
    const setupAlertDismissed = ref(false);
    const dismissKey = `sticker-setup-alert-dismissed-${orgId}`;
    try {
      setupAlertDismissed.value = localStorage.getItem(dismissKey) === '1';
    } catch (e) {
      // storage can be unavailable; the alert just shows again
    }
    const showSetupAlert = computed(() => !stripeOnboarded.value && !setupAlertDismissed.value);

    function dismissSetupAlert() {
      setupAlertDismissed.value = true;
      try {
        localStorage.setItem(dismissKey, '1');
      } catch (e) {
        // ignore
      }
    }

    async function loadStripeStatus() {
      try {
        const org = await getOrganization(orgId);
        stripeOnboarded.value = !!org?.stripe_onboarded;
      } catch (e) {
        stripeOnboarded.value = true; // unknown: don't nag
      }
    }

    const TYPE_LABELS = {
      paid: 'Paid',
      campaign: 'Campaign',
      free: 'Free',
      sweepstakes: 'Sweepstakes',
      ticket: 'Ticket',
    };
    const typeKey = (value) => String(value || '').toLowerCase();
    const typeLabel = (value) => TYPE_LABELS[typeKey(value)] || value || 'Sticker';
    const typeClass = (value) => (
      `type-badge--${TYPE_LABELS[typeKey(value)] ? typeKey(value) : 'paid'}`
    );

    function duration(item) {
      if (!item.endDate) return { label: 'Evergreen', tone: 'green' };
      const end = dayjs(item.endDate);
      if (end.isBefore(dayjs())) return { label: 'Ended', tone: 'gray' };
      if (end.diff(dayjs(), 'day') <= 7) return { label: 'Ending Soon', tone: 'red' };
      return { label: 'Active', tone: 'gray' };
    }

    const formatNumber = (n) => new Intl.NumberFormat('en-US').format(Number(n) || 0);
    const formatCurrency = (n) => new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(Number(n) || 0);
    const formatDate = (value) => (value ? dayjs(value).format('MMM D, YYYY') : '—');

    function exportCsv() {
      const header = ['Title', 'Status', 'Type', 'Shares', 'Gross Raised', 'Net Raised',
        'Impressions', 'Duration', 'GLOW', 'Affiliates', 'Created', 'Share Link'];
      const quote = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const rows = filteredItems.value.map((s) => [
        s.title, stickerState(s), typeLabel(s.campaignType), s.shareCount || 0,
        s.grossRaised || 0, s.netRaised || 0, s.viewCount || 0, duration(s).label,
        s.glow ? 'GLOW' : 'No GLOW', s.affiliatesCount || 0, formatDate(s.createdAt),
        getStickerShortUrl(s),
      ]);
      const csv = [header, ...rows].map((r) => r.map(quote).join(',')).join('\n');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      link.download = `stickers-${dayjs().format('YYYY-MM-DD')}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    }

    onMounted(async () => {
      loadStripeStatus();
      await getItems();
    });

    return {
      orgId,
      stripeOnboarded,
      showSetupAlert,
      dismissSetupAlert,
      typeLabel,
      typeClass,
      duration,
      formatNumber,
      formatCurrency,
      formatDate,
      exportCsv,
      items,
      loading,
      dialog,
      state,
      selected,
      sortOptions,
      setAsOption,
      setAs,
      filteredItems,
      saveItem,
      stickerState,
      onBoardOnStripe,
      headerFilter,
      handleSortChange,
      onCheckboxChange,
      bulkUpdate,
      getButtonClass,
      tableRef,
      viewTabs,
      getStickerShortUrl,
      copyShareLink,
      goCollections,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-stickers-page {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }

  &__header-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

}

// Action buttons rely on the global CMS theme
.c-stickers-action-btn {
  :deep(.me-2) {
    margin-right: 0.5rem;
  }
}

// Stickers / Collections switch at the start of the toolbar
.c-stickers-toolbar-divider {
  width: 1px;
  height: 24px;
  background: rgba(0, 0, 0, 0.1);
}

.c-stickers-bulk-select {
  width: 180px;
}

// Navigation Tabs (Pill Style)
.c-stickers-nav {
  display: inline-flex;
  height: 36px;
  align-items: center;
  justify-content: center;
  background-color: #ececf0;
  border-radius: 14px;
  padding: 3px;
  gap: 0;
  flex-wrap: nowrap;
  overflow-x: auto;
}

.c-stickers-nav-link {
  display: inline-flex;
  height: calc(100% - 1px);
  align-items: center;
  justify-content: center;
  padding: 4px 16px;
  border-radius: 14px;
  border: 1px solid transparent;
  font-size: 0.875rem;
  font-weight: 500;
  color: #0a0a0a;
  background: transparent;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;
  white-space: nowrap;
  user-select: none;

  &:hover:not(:disabled):not(&--active) {
    background-color: rgba(255, 255, 255, 0.5);
  }

  &--active {
    background-color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.c-stickers-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

// Table Section
.c-stickers-table-section {
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: none;
  overflow-x: auto;

  :deep(.el-table) {
    min-width: 100%;
    border-radius: 0;

    .cell {
      color: #0a0a0a;
      font-weight: 400;
      font-size: 0.875rem;
      line-height: 1.25rem;
      padding: 0.5rem 0.5rem;
    }

    th > .cell {
      color: #0a0a0a;
      font-weight: 500;
      font-size: 0.875rem;
      text-transform: none;
      letter-spacing: normal;
      padding: 0.5rem 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: transparent;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      padding: 0.5rem 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      padding: 0.625rem 0;
    }

    tbody tr {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: rgba(236, 236, 240, 0.5);
      }
    }

    .el-table__empty-block {
      min-height: 240px;
    }
  }
}

// Skeleton Loader
.c-skeleton-loader {
  width: 100%;
  padding: 1rem 0;
}

.c-skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  gap: 1rem;
  border-bottom: 1px solid $gray-100;

  &:last-child {
    border-bottom: none;
  }
}

.c-skeleton-cell {
  height: 20px;
  background: linear-gradient(90deg, #F0F0F0 25%, #E0E0E0 50%, #F0F0F0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;

  &--checkbox {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &--image {
    width: 50px;
    height: 50px;
    flex-shrink: 0;
    border-radius: 8px;
  }

  &--title {
    width: 180px;
    flex-shrink: 0;
  }

  &--type {
    width: 100px;
    flex-shrink: 0;
  }

  &--status {
    width: 120px;
    flex-shrink: 0;
  }

  &--link {
    width: 140px;
    flex-shrink: 0;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Table Cell Styles
.sticker-image-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
}

.sticker-image {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
}

.font-black {
  font-weight: 500;
  color: #0a0a0a;
}

.font-medium {
  font-weight: 500;
  color: $gray-700;
}

.lh-sm {
  line-height: 1.25;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border: 1px solid;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1rem;
  white-space: nowrap;

  &--approved {
    background-color: #c3eae3;
    color: #005c6b;
    border-color: #01beb0;
  }

  &--pending {
    background-color: #fef7e0;
    color: #8a7800;
    border-color: #ffdb0f;
  }

  &--archived {
    background-color: #f3f4f6;
    color: #4b5563;
    border-color: #d1d5db;
  }
}

.share-link-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.share-link-text {
  font-size: 12px;
  color: $gray-600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.copy-icon {
  color: #909399;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;
  transition: color 0.3s;

  &:hover {
    color: #01beb0;
  }
}

// Error State
.c-stickers-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);

  &__icon {
    color: #f56c6c;
    margin-bottom: 1rem;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0 0 0.5rem 0;
  }

  &__text {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0 0 1.5rem 0;
  }

}

// Responsive Design
@media (max-width: 768px) {
  .c-stickers-page {
    padding: 1.5rem;

    &__heading {
      font-size: 1.5rem;
    }

    &__header {
      gap: 1rem;
    }
  }

}

.type-badge,
.duration-badge {
  display: inline-block;
  padding: 2px 10px;
  border: 1px solid;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
}
.type-badge--paid,
.type-badge--sweepstakes { background: #e8e9fd; color: #4349dd; border-color: #4349dd; }
.type-badge--campaign,
.type-badge--ticket { background: #ffe0ee; color: #ff6b9d; border-color: #ff6b9d; }
.type-badge--free { background: #ffecd9; color: #ea580c; border-color: #f97316; }
.duration-badge--green { background: #c3eae3; color: #005c6b; border-color: #01beb0; }
.duration-badge--red { background: #fff; color: #cc2900; border-color: #f43300; }
.duration-badge--gray { background: #f9fafb; color: #6b7280; border-color: #e5e7eb; }
</style>
